import { i18n } from '@/i18n';
import {
  AccountNotifications,
  Credentials,
  NotificationSettings,
  PaymentCredentials,
  PayoutOptionType,
  PersonalInfo,
  SaveNotificationsResponse,
} from '@/models/accountModels';
import {
  AccountActivationResponse,
  AmbassadorProfile,
  AuthReducerPropsType,
  AuthUser,
  ChangePasswordResponse,
  LoginValidationResponse,
  RegisterResponse,
  ResetPasswordResponse,
  SaveAmbassadorResponse,
  UserAccount,
} from '@/models/authModels';
import { Channel } from '@/models/channelModels';
import { CompanyAccount } from '@/models/companyModels';
import { ServerStatus, SideBarUserData, StatsDashboardData } from '@/models/globalModels';

import { RootState } from '../../rootReducer';
import * as t from '../types';

const globalState: AuthReducerPropsType = {
  loginStatus: ServerStatus.IDLE,
  userStatus: ServerStatus.IDLE,
  forgotPasswordStatus: ServerStatus.IDLE,
  ambassadorStatus: ServerStatus.IDLE,
  registerStatus: ServerStatus.IDLE,
  isAccountActive: true,
  user: {} as UserAccount,
  channel: Channel.DEFAULT as Channel,
};

export type PossibleActions =
  | { type: 'USER_SSO_FETCH'; payload: AuthUser }
  | { type: 'USER_SSO_FETCH_ERROR'; payload: any }
  | { type: 'USER_FETCH'; payload: UserAccount }
  | { type: 'USER_FETCHING' }
  | { type: 'USER_FETCH_ERROR'; payload: any }
  | { type: 'LOGIN_FETCH' }
  | { type: 'LOGIN_FETCHING' }
  | { type: 'LOGIN_FETCH_ERROR'; payload: any }
  | { type: 'COMPANY_FETCHING' }
  | { type: 'COMPANY_FETCH'; payload: CompanyAccount }
  | { type: 'COMPANY_FETCH_ERROR'; payload: any }
  | { type: 'AMBASSADOR_FETCH'; payload: AmbassadorProfile }
  | { type: 'AMBASSADOR_FETCHING' }
  | { type: 'AMBASSADOR_FETCH_ERROR'; payload: any }
  | { type: 'ACCT_NOTIF_FETCH'; payload: AccountNotifications }
  | { type: 'ACCT_NOTIF_FETCHING' }
  | { type: 'ACCT_NOTIF_FETCH_ERROR'; payload: any }
  | { type: 'HYDRATE_MY_ACCT' }
  | { type: 'SAVE_AMBASSADOR_FETCH'; payload: SaveAmbassadorResponse }
  | { type: 'SAVE_AMBASSADOR_FETCHING' }
  | { type: 'SAVE_AMBASSADOR_FETCH_ERROR'; payload: any }
  | { type: 'SAVE_NOTIFICATIONS_FETCH'; payload: SaveNotificationsResponse }
  | { type: 'SAVE_NOTIFICATIONS_FETCHING' }
  | { type: 'SAVE_NOTIFICATIONS_FETCH_ERROR'; payload: any }
  | { type: 'CHANGE_PASSWORD_FETCH'; payload: ChangePasswordResponse }
  | { type: 'CHANGE_PASSWORD_FETCHING' }
  | { type: 'CHANGE_PASSWORD_FETCH_ERROR'; payload: ChangePasswordResponse }
  | { type: 'DEACTIVATE_ACCOUNT_FETCH'; payload: AccountActivationResponse }
  | { type: 'DEACTIVATE_ACCOUNT_FETCHING' }
  | { type: 'DEACTIVATE_ACCOUNT_FETCH_ERROR'; payload: any }
  | { type: 'ACTIVATE_ACCOUNT_FETCH'; payload: AccountActivationResponse }
  | { type: 'ACTIVATE_ACCOUNT_FETCHING' }
  | { type: 'ACTIVATE_ACCOUNT_FETCH_ERROR'; payload: any }
  | { type: 'RESET_LOGIN_STATUS' }
  | { type: 'FORGOT_PASSWORD_FETCHING' }
  | { type: 'FORGOT_PASSWORD_FETCH'; payload: any }
  | { type: 'FORGOT_PASSWORD_FETCH_ERROR' }
  | { type: 'FORGOT_PASSWORD_CLEAN' }
  | { type: 'ACTIVITY_FETCH'; payload: any }
  | { type: 'ACTIVITY_FETCHING' }
  | { type: 'ACTIVITY_FETCH_ERROR'; payload: any }
  | { type: 'REGISTER_FETCH'; payload: RegisterResponse }
  | { type: 'REGISTER_FETCHING' }
  | { type: 'REGISTER_FETCH_ERROR'; payload: any }
  | { type: 'RESET_PASSWORD_FETCHING' }
  | { type: 'RESET_PASSWORD_FETCH'; payload: ResetPasswordResponse }
  | { type: 'RESET_PASSWORD_FETCH_ERROR'; payload: any }
  | { type: 'LOGOUT' }
  | { type: 'SET_PORTAL_CHANNEL'; payload: any };

const reducer = (state = globalState, action: PossibleActions): AuthReducerPropsType => {
  // when no acct activation error msg comes from the API
  const authActivationError: LoginValidationResponse = {
    non_field_errors: [i18n.t('auth:messages.changeAccountStatusError')],
    password: [],
    email: [],
  };

  let error;
  switch (action.type) {
    case t.USER_SSO_FETCH:
      return {
        ...state,
        userSSO: action.payload,
      };
    case t.USER_SSO_FETCH_ERROR:
      return {
        ...state,
        userSSO: undefined,
      };
    case t.LOGIN_FETCH:
      return {
        ...state,
        loginStatus: ServerStatus.FETCH,
      };
    case t.LOGIN_FETCHING:
      return {
        ...state,
        loginStatus: ServerStatus.FETCHING,
        loginStatusMessage: undefined,
      };
    case t.LOGIN_FETCH_ERROR:
      error = action.payload;
      return {
        ...state,
        loginStatus: ServerStatus.FETCH_ERROR,
        loginStatusMessage: error.message,
        authValidationResponse: error.apiResponse,
        error: error,
      };
    case t.LOGOUT:
      return {
        ...state,
        loginStatus: ServerStatus.IDLE,
        ambassadorStatus: ServerStatus.IDLE,
        loginStatusMessage: '',
      };
    case t.USER_FETCH:
      const fetchUser = Object.assign({}, action.payload);
      return {
        ...state,
        userStatus: ServerStatus.FETCH,
        user: fetchUser,
      };
    case t.USER_FETCHING:
      return {
        ...state,
        userStatus: ServerStatus.FETCHING,
      };
    case t.USER_FETCH_ERROR:
      error = action.payload;
      return {
        ...state,
        userStatus: ServerStatus.FETCH_ERROR,
        loginStatusMessage: error.message,
        error: error,
      };
    case t.COMPANY_FETCHING:
      return {
        ...state,
        companyStatus: ServerStatus.FETCHING,
      };
    case t.COMPANY_FETCH:
      const fetchCompany = Object.assign({}, action.payload);
      return {
        ...state,
        companyStatus: ServerStatus.FETCH,
        companyAccount: fetchCompany,
      };
    case t.COMPANY_FETCH_ERROR:
      error = action.payload;
      return {
        ...state,
        companyStatus: ServerStatus.FETCH_ERROR,
        authStatusMessage: error.message,
        error: error,
      };
    case t.AMBASSADOR_FETCHING:
      return {
        ...state,
        ambassadorStatus: ServerStatus.FETCHING,
      };
    case t.AMBASSADOR_FETCH:
      const ambassador = Object.assign({}, action.payload);
      return {
        ...state,
        ambassadorStatus: ServerStatus.FETCH,
        ambassadorProfile: ambassador,
        isAccountActive: !ambassador.deactivated_by_self,
      };
    case t.AMBASSADOR_FETCH_ERROR:
      error = action.payload;
      return {
        ...state,
        ambassadorStatus: ServerStatus.FETCH_ERROR,
        authStatusMessage: error.message,
        error: error,
      };
    case t.ACCT_NOTIF_FETCHING:
      return {
        ...state,
        notificationsStatus: ServerStatus.FETCHING,
      };
    case t.ACCT_NOTIF_FETCH:
      const notifications = Object.assign({}, action.payload);
      return {
        ...state,
        notificationsStatus: ServerStatus.FETCH,
        accountNotifications: notifications,
      };
    case t.ACCT_NOTIF_FETCH_ERROR:
      error = action.payload;
      return {
        ...state,
        notificationsStatus: ServerStatus.FETCH_ERROR,
        authStatusMessage: error.message,
        error: error,
      };
    case t.HYDRATE_MY_ACCT:
      return {
        saveAmbassadorMessage: undefined,
        changePasswordMessage: undefined,
        saveAmbassadorStatus: ServerStatus.IDLE,
        changePasswordStatus: ServerStatus.IDLE,
        saveNotificationsStatus: ServerStatus.IDLE,
        ...state,
      };
    case t.CHANGE_PASSWORD_FETCHING:
      return {
        ...state,
        changePasswordStatus: ServerStatus.FETCHING,
        changePasswordMessage: undefined,
      };
    case t.CHANGE_PASSWORD_FETCH:
      const changePassMsg = Object.assign({}, action.payload);
      return {
        ...state,
        changePasswordStatus: ServerStatus.FETCH,
        changePasswordMessage: changePassMsg.detail,
      };
    case t.CHANGE_PASSWORD_FETCH_ERROR:
      const changePassError = Object.assign({}, action.payload);
      return {
        ...state,
        changePasswordStatus: ServerStatus.FETCH_ERROR,
        changePasswordMessage: changePassError.detail,
      };
    case t.SAVE_AMBASSADOR_FETCHING:
      return {
        ...state,
        saveAmbassadorStatus: ServerStatus.FETCHING,
        saveAmbassadorMessage: undefined,
      };
    case t.SAVE_AMBASSADOR_FETCH:
      const updatedProfile = Object.assign({}, action.payload);
      return {
        ...state,
        saveAmbassadorStatus: ServerStatus.FETCH,
        saveAmbassadorMessage: i18n.t('auth:messages.accountChangesSaved'),
        ambassadorProfile: updatedProfile,
      };
    case t.SAVE_AMBASSADOR_FETCH_ERROR:
      error = action.payload;
      return {
        ...state,
        saveAmbassadorStatus: ServerStatus.FETCH_ERROR,
        saveAmbassadorMessage: i18n.t('auth:messages.accountChangesError'),
        error: error,
      };
    case t.SAVE_NOTIFICATIONS_FETCHING:
      return {
        ...state,
        saveNotificationsStatus: ServerStatus.FETCHING,
        saveAmbassadorMessage: undefined,
      };
    case t.SAVE_NOTIFICATIONS_FETCH:
      const updatedNotifications = Object.assign({}, action.payload);
      return {
        ...state,
        saveNotificationsStatus: ServerStatus.FETCH,
        saveAmbassadorMessage: i18n.t('auth:messages.accountChangesSaved'),
        accountNotifications: updatedNotifications,
      };
    case t.SAVE_NOTIFICATIONS_FETCH_ERROR:
      error = action.payload;
      return {
        ...state,
        saveAmbassadorStatus: ServerStatus.FETCH_ERROR,
        saveAmbassadorMessage: i18n.t('auth:messages.accountChangesError'),
        error: error,
      };
    case t.DEACTIVATE_ACCOUNT_FETCHING:
      return {
        ...state,
        changeAccountStatus: ServerStatus.FETCHING,
      };
    case t.DEACTIVATE_ACCOUNT_FETCH:
      const deactivateResponse = Object.assign({}, action.payload);
      return {
        ...state,
        changeAccountStatus: ServerStatus.FETCH,
        isAccountActive: !deactivateResponse.deactivated_by_self,
      };
    case t.DEACTIVATE_ACCOUNT_FETCH_ERROR:
      error = action.payload;
      return {
        ...state,
        changeAccountStatus: ServerStatus.FETCH_ERROR,
        authValidationResponse: authActivationError,
        isAccountActive: true,
        error: error,
      };
    case t.ACTIVITY_FETCH:
      return {
        ...state,
        activitiesStatus: ServerStatus.FETCH,
        activities: action.payload,
      };
    case t.ACTIVITY_FETCHING:
      return {
        ...state,
        activitiesStatus: ServerStatus.FETCHING,
      };
    case t.ACTIVITY_FETCH_ERROR:
      error = action.payload;
      return {
        ...state,
        activitiesStatus: ServerStatus.FETCH_ERROR,
        activitiesStatusMessage: error.message,
        error: error,
      };
    case t.ACTIVATE_ACCOUNT_FETCHING:
      return {
        ...state,
        changeAccountStatus: ServerStatus.FETCHING,
      };
    case t.ACTIVATE_ACCOUNT_FETCH:
      const activateResponse = Object.assign({}, action.payload);
      return {
        ...state,
        changeAccountStatus: ServerStatus.FETCH,
        isAccountActive: !activateResponse.deactivated_by_self,
      };
    case t.ACTIVATE_ACCOUNT_FETCH_ERROR:
      error = action.payload;
      return {
        ...state,
        changeAccountStatus: ServerStatus.FETCH_ERROR,
        authValidationResponse: authActivationError,
        ambassadorStatus: ServerStatus.IDLE,
        userStatus: ServerStatus.IDLE,
        isAccountActive: true,
        error: error,
      };
    case t.RESET_LOGIN_STATUS:
      return {
        ...state,
        ambassadorStatus: ServerStatus.IDLE,
        loginStatus: ServerStatus.IDLE,
        userStatus: ServerStatus.IDLE,
        ambassadorProfile: undefined,
        user: {} as UserAccount,
        isAccountActive: true,
      };
    case t.FORGOT_PASSWORD_FETCHING:
      return {
        ...state,
        forgotPasswordStatus: ServerStatus.FETCHING,
        isEmailSent: undefined,
        forgotPasswordStatusMessage: undefined,
      };
    case t.FORGOT_PASSWORD_FETCH:
      return {
        ...state,
        forgotPasswordStatus: ServerStatus.FETCH,
        isEmailSent: action.payload,
      };
    case t.FORGOT_PASSWORD_FETCH_ERROR:
      return {
        ...state,
        forgotPasswordStatus: ServerStatus.FETCH_ERROR,
        forgotPasswordStatusMessage: 'Something went wrong. Please try again later. - (API)',
      };
    case t.FORGOT_PASSWORD_CLEAN:
      return {
        ...state,
        forgotPasswordStatus: ServerStatus.IDLE,
        forgotPasswordStatusMessage: undefined,
        isEmailSent: false,
      };
    case t.REGISTER_FETCHING:
      return {
        ...state,
        registerStatus: ServerStatus.FETCHING,
        registerStatusMessage: undefined,
        registerResponse: undefined,
      };
    case t.REGISTER_FETCH:
      const registerResponse = Object.assign({}, action.payload);
      return {
        ...state,
        registerStatus: ServerStatus.FETCH,
        registerStatusMessage: i18n.t('auth:messages.registerAccount'),
        registerResponse: registerResponse,
      };
    case t.REGISTER_FETCH_ERROR:
      const registerError = Object.assign({}, action.payload);
      // checks for required field message from the API to show pre-defined form validation text from user messages
      const registerErrorMsg = registerError.message || i18n.t('common:formValidation.formRequiredField');
      return {
        ...state,
        registerStatus: ServerStatus.FETCH_ERROR,
        registerStatusMessage: registerErrorMsg ? registerErrorMsg : i18n.t('auth:messages.registerAccountError'),
        registerResponse: registerError.apiResponse,
        error: registerError,
      };
    case t.RESET_PASSWORD_FETCHING:
      return {
        ...state,
        resetPasswordStatus: ServerStatus.FETCHING,
        resetPasswordStatusMessage: undefined,
      };
    case t.RESET_PASSWORD_FETCH:
      return {
        ...state,
        resetPasswordStatus: ServerStatus.FETCH,
        resetPasswordStatusMessage: i18n.t('auth:messages.resetPasswordSuccess'),
      };
    case t.RESET_PASSWORD_FETCH_ERROR:
      const resetPassError = action.payload;
      return {
        ...state,
        resetPasswordStatus: ServerStatus.FETCH_ERROR,
        resetPasswordStatusMessage: resetPassError?.detail || i18n.t('common:formValidation.resetPasswordError'),
      };
    case t.SET_PORTAL_CHANNEL:
      return {
        ...state,
        channel: action.payload as Channel,
      };
    default:
      return {
        ...state,
      };
  }
};

// SELECTORS
export const selectSideBarProfileData = (state: RootState): SideBarUserData => {
  const profile = state.auth.ambassadorProfile;
  const createdAt = profile
    ? new Date(profile.created_at).toLocaleDateString([], { month: 'numeric', day: 'numeric', year: 'numeric' })
    : '';

  return profile
    ? {
        avatar_url: profile.avatar_url,
        first_name: profile.first_name,
        last_name: profile.last_name,
        email: profile.email,
        created_at: createdAt,
        username: profile.username,
        memorable_url: profile.memorable_url,
        facebook_url: profile.facebook_url,
        twitter_url: profile.twitter_url,
        linkedin_url: profile.linkedin_url,
        pinterest_url: profile.pinterest_url,
      }
    : ({} as SideBarUserData);
};

// TODO: move selector to proper stats/customization reducer
export const selectStatsDashboardData = (state: RootState): StatsDashboardData => {
  const profile = state.auth.ambassadorProfile;
  const company = state.auth.companyAccount;
  const campaign = state.campaigns.selectedCampaign;

  return profile && company
    ? {
        total_money_earned: profile.total_money_earned,
        total_points_earned: profile.total_points_earned,
        money_pending: profile.money_pending,
        current_commission_balance: profile.current_commission_balance,
        money_paid: profile.money_paid,
        point_name: company.point_name,
        currency: company.currency,
        has_non_monetary: company.has_non_monetary,
        commission_type: campaign?.commission_type,
      }
    : ({} as StatsDashboardData);
};

export const selectPayoutOptions = (state: RootState): PayoutOptionType[] => {
  const payoutOptions = state.auth.companyAccount?.payout_preference_options;
  if (!payoutOptions) return [] as PayoutOptionType[];
  // converts payout options obj to PayoutOptionType array
  const options = Object.keys(payoutOptions).map(
    (i) =>
      <PayoutOptionType>{
        payoutId: parseInt(i),
        payoutName: payoutOptions[parseInt(i)],
      },
  );
  return options;
};

// MY ACCT PAGE
export const selectCredentialsData = (state: RootState): Credentials => {
  const profile = state.auth.ambassadorProfile;

  return profile
    ? {
        email: profile.email,
        username: profile.username,
      }
    : ({} as Credentials);
};

export const selectPersonalInfoData = (state: RootState): PersonalInfo => {
  const profile = state.auth.ambassadorProfile;

  return profile
    ? {
        firstName: profile.first_name,
        lastName: profile.last_name,
        company: profile.company,
        street: profile.street,
        city: profile.city,
        state: profile.state,
        zip: profile.zip,
        country: profile.country,
        phone: profile.phone,
        date_of_birth: profile.date_of_birth,
      }
    : ({} as PersonalInfo);
};

export const selectPaymentCredentialsData = (state: RootState): PaymentCredentials => {
  const profile = state.auth.ambassadorProfile;

  const preferredPayment: PayoutOptionType = {
    payoutId: profile?.payout_preference,
    payoutName: profile?.payout_preference_name,
  };
  return profile
    ? {
        preferredPayment: preferredPayment,
        paypalEmail: profile.paypal_email,
        dwollaEmail: profile.dwolla_email,
      }
    : ({} as PaymentCredentials);
};

export const selectNotificationsData = (state: RootState): NotificationSettings => {
  const notifications = state.auth.accountNotifications;
  const auth = state.auth;

  return notifications && auth.ambassadorProfile
    ? {
        emailPreferences: auth.ambassadorProfile.email_preferences,
        uid: auth.ambassadorProfile.uid,
        accountNotifications: auth.accountNotifications,
      }
    : ({} as NotificationSettings);
};

export const selectPortalChannel = (state: RootState): Channel => state.auth.channel;

export default reducer;
